/* eslint-disable no-new */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import NumberStepper from 'client/javascript/number_stepper';
import { getSelfServeIntl } from 'client/self_serve/shared/self_serve.intl';
import { lockScroll, unlockScroll } from 'client/javascript/lock_scroll';
import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';
import { initDatePicker } from 'client/self_serve/util/utils';
import { getDefaultStartDate, createLocalDate, formatStartDateForIQS } from 'shared/util/date';
import {
  OVERLAY_OPEN_LABEL,
  CAPACITY_OVERLAY_CLEARED_LABEL,
  CAPACITY_OVERLAY_INTERACTION_LABEL,
  DATEPICKER_FILTER_NAME,
  CAPACITY_FILTER_NAME
} from 'client/self_serve/components/page/analytics';
import { trackInteraction } from 'client/self_serve/services/analytics';
import FilterChip from './FilterChip';
import messages from './messages';

const selfServeIntl = getSelfServeIntl();

export const DEFAULT_CAPACITY = '1';

class FilterChips extends Component {
  constructor(props) {
    super(props);
    this.numberStepperRef = React.createRef();
    this.datePickerInputRef = React.createRef();

    let selectedStartDate = createLocalDate(props.selectedStartDate);
    selectedStartDate = isNaN(Date.parse(selectedStartDate)) ? null : selectedStartDate; // eslint-disable-line no-restricted-globals

    this.state = {
      selectedStartDate: selectedStartDate || getDefaultStartDate(),
      selectedCapacity: props.selectedCapacity || DEFAULT_CAPACITY,
      inputCapacity: props.selectedCapacity || DEFAULT_CAPACITY,
      isCapacityFilterOpen: false,
      isStartDateFilterOpen: false
    };
  }

  componentDidMount() {
    new NumberStepper(this.numberStepperRef.current, {
      onChange: value => this.setState({ inputCapacity: value })
    });
    this.datePicker = initDatePicker({
      element: this.datePickerInputRef.current,
      onChange: this.handleDatePickerChange,
      defaultDate: this.state.selectedStartDate
    });
  }

  componentDidUpdate(prevProps) {
    const { selectedStartDate, selectedCapacity } = this.props;
    const didQueryChange =
      prevProps.selectedStartDate !== selectedStartDate || prevProps.selectedCapacity !== selectedCapacity;
    if (didQueryChange) {
      const date = createLocalDate(selectedStartDate);
      this.setState({
        selectedStartDate: date,
        selectedCapacity
      });
      this.datePicker.setDate(date);
    }
  }

  handleSubmitFilters = () => {
    const { onSubmit } = this.props;
    if (typeof onSubmit === 'function') {
      onSubmit({
        capacity: this.state.selectedCapacity,
        startDate: formatStartDateForIQS(this.state.selectedStartDate)
      });
    }
  };

  closeOverlaysAndSubmitFilters = () => {
    this.setState({
      isCapacityFilterOpen: false,
      isStartDateFilterOpen: false
    });
    User.saveValue(USER_STORAGE_KEY_MAP.capacity, this.state.selectedCapacity);
    User.saveValue(USER_STORAGE_KEY_MAP.startDateFilter, formatStartDateForIQS(this.state.selectedStartDate));

    unlockScroll();
    this.handleSubmitFilters();
  };

  areAnyFiltersOpen = () => {
    const { isCapacityFilterOpen, isStartDateFilterOpen } = this.state;
    return isCapacityFilterOpen || isStartDateFilterOpen;
  };

  /* Capacity */
  handleClearCapacityFilter = () => {
    trackInteraction(CAPACITY_OVERLAY_CLEARED_LABEL);
    this.setState(
      {
        selectedCapacity: DEFAULT_CAPACITY,
        inputCapacity: DEFAULT_CAPACITY
      },
      this.closeOverlaysAndSubmitFilters
    );
  };

  handleApplyCapacityFilter = () => {
    this.setState(state => ({ selectedCapacity: state.inputCapacity || 1 }), this.closeOverlaysAndSubmitFilters);
  };

  handleOpenCapacityFilter = () => {
    trackInteraction(OVERLAY_OPEN_LABEL, { filterName: CAPACITY_FILTER_NAME });
    this.setState({ isCapacityFilterOpen: true });
    lockScroll();
  };

  handleCapacityInputChange = e => {
    const cleanValue = String(e.target.value).replace(new RegExp('[^0-9]', 'gm'), '');
    const intValue = parseInt(cleanValue, 10);
    e.target.value = intValue === 0 ? 1 : intValue;
    trackInteraction(CAPACITY_OVERLAY_INTERACTION_LABEL, {
      filterValue: e.target.value,
      filterName: CAPACITY_FILTER_NAME
    });
    this.setState({ inputCapacity: e.target.value });
  };

  /* Start Date */
  handleOpenStartDateFilter = () => {
    trackInteraction(OVERLAY_OPEN_LABEL, { filterName: DATEPICKER_FILTER_NAME });
    this.setState({ isStartDateFilterOpen: true });
    lockScroll();
  };

  handleDatePickerChange = date => {
    this.setState({ selectedStartDate: date[0] }, this.closeOverlaysAndSubmitFilters);
  };

  openCapacityFilterWithSpacebar = event => {
    if (event.code === 'Space' || event.key === ' ') {
      if (!this.state.isCapacityFilterOpen) {
        this.handleOpenCapacityFilter();
        event.preventDefault();
      } else {
        this.setState({ isCapacityFilterOpen: false });
      }
    }
  };

  openDatePickerWithSpacebar = event => {
    if (event.code === 'Space' || event.key === ' ') {
      if (!this.state.isStartDateFilterOpen) {
        this.handleOpenStartDateFilter();
        event.preventDefault();
      } else {
        this.setState({ isStartDateFilterOpen: false });
      }
    }
  };

  /* Render */
  renderDateChip = () => {
    const { selectedStartDate, isStartDateFilterOpen } = this.state;
    const ariaLabel = selfServeIntl.formatMessage(messages.datePickerAriaLabel);
    const ariaLabelForInput = selfServeIntl.formatMessage(messages.datePickerAriaLabelForInput);

    return (
      <FilterChip
        isActive={!!selectedStartDate}
        isOpen={isStartDateFilterOpen}
        openFilter={this.handleOpenStartDateFilter}
        openFilterWithSpacebar={this.openDatePickerWithSpacebar}
        buttonTitle={selfServeIntl.formatDate(new Date(selectedStartDate), {
          month: 'short',
          day: '2-digit',
          year: 'numeric'
        })}
        className="self-serve__datepicker-chip"
        id="date"
        ariaLabel={ariaLabel}
      >
        <input aria-label={ariaLabelForInput} hidden ref={this.datePickerInputRef} />
      </FilterChip>
    );
  };

  renderCapacityChip = () => {
    const { selectedCapacity, inputCapacity, isCapacityFilterOpen } = this.state;

    const capacityChipTitle = `${selectedCapacity} ${
      selectedCapacity > 1
        ? selfServeIntl.formatMessage(messages.capacityFilterChipLabelPlural)
        : selfServeIntl.formatMessage(messages.capacityFilterChipLabelSingular)
    }`;

    const ariaLabel = selfServeIntl.formatMessage(messages.capacityFilterChipAriaLabel);
    const ariaLabelForInput = selfServeIntl.formatMessage(messages.capacityFilterChipAriaLabelInput);

    return (
      <FilterChip
        buttonTitle={capacityChipTitle}
        overlayTitle={selfServeIntl.formatMessage(messages.capacityFilterOverlay)}
        isActive={!!selectedCapacity}
        isOpen={isCapacityFilterOpen}
        openFilterWithSpacebar={this.openCapacityFilterWithSpacebar}
        openFilter={this.handleOpenCapacityFilter}
        clearFilter={this.handleClearCapacityFilter}
        applyFilter={this.handleApplyCapacityFilter}
        showClearApplyButtons
        className="self-serve__filter-chip"
        id="capacity"
        ariaLabel={ariaLabel}
      >
        <div
          ref={this.numberStepperRef}
          className="number-stepper number-stepper--ray ray-text-field"
          id="capacity-number-stepper"
        >
          <input
            onChange={this.handleCapacityInputChange}
            className="ray-text-field__input"
            data-test-id="desiredCapacity"
            id="desiredCapacity"
            min="1"
            type="number"
            value={inputCapacity}
            name="desiredCapacity"
            aria-required="true"
            required
            aria-label={ariaLabelForInput}
          />
          <button className="number-stepper-decrease" aria-label="decrease number">
            -
          </button>
          <button className="number-stepper-increase" aria-label="increase number">
            +
          </button>
        </div>
      </FilterChip>
    );
  };

  render() {
    return (
      <div
        className={cn('self-serve__filter-chips', {
          'filter-overlay-active filter-overlay-animating': this.areAnyFiltersOpen()
        })}
      >
        <div className="filter-overlay-background" onClick={this.handleApplyCapacityFilter} />
        {this.renderCapacityChip()}
        {this.renderDateChip()}
      </div>
    );
  }
}

FilterChips.propTypes = {
  onSubmit: PropTypes.func,
  selectedCapacity: PropTypes.string,
  selectedStartDate: PropTypes.string
};

export default FilterChips;
